import type { Folder } from "~/features/integration/sharepoint/schema";
import {
  fidoDocumentsQuery,
  sharepointDocumentsQuery,
} from "~/features/query-cache/queries";
import type { DocumentsData } from "~/routes/api.resources.documents.$origin";
import { fetchJson } from "~/utils/fetch-json";

import { useQueries, useQuery } from "@tanstack/react-query";
import { toMilliseconds } from "duration-fns";

export const useDocumentQueries = () => {
  const getFidoReserveDocuments = () =>
    fetchJson<DocumentsData>(
      "/api/resources/documents/sharepoint-train_operation-fido_reserve",
      {
        timeoutMs: toMilliseconds({
          seconds: 60,
        }),
      },
    );

  const getTrainOperationDocuments = () =>
    fetchJson<DocumentsData>(
      "/api/resources/documents/sharepoint-train_operation-documents",
      {
        timeoutMs: toMilliseconds({
          seconds: 60,
        }),
      },
    );

  return {
    trainOperationDocuments: {
      queryFn: getTrainOperationDocuments,
      ...sharepointDocumentsQuery(),
    },
    fidoReserveDocuments: {
      queryFn: getFidoReserveDocuments,
      ...fidoDocumentsQuery(),
    },
  };
};

export const useTrainOperationDocuments = () => {
  const { trainOperationDocuments } = useDocumentQueries();

  return useQuery({
    ...trainOperationDocuments,
  });
};

export const useFidoReserveDocuments = () => {
  const { fidoReserveDocuments } = useDocumentQueries();

  return useQuery({
    ...fidoReserveDocuments,
  });
};

export const useFileTree = () => {
  const queries = useDocumentQueries();

  return useQueries({
    queries: [queries.trainOperationDocuments, queries.fidoReserveDocuments],
    combine(results) {
      if (results.length !== 2) {
        // FIXME: Not sure why it happens, but the array is at some point empty, which seems weird. If we can find out why and eliminate the cause, we should be able to remove this check.
        return undefined;
      }

      const [trainOperation, fidoReserve] = results;
      if (!trainOperation.data || !fidoReserve.data) {
        return undefined;
      }

      return {
        type: "folder",
        name: "root",
        contents: [...trainOperation.data.contents, fidoReserve.data],
      } satisfies Folder;
    },
  });
};
